import { memo, useMemo, type ReactNode } from "react"
import { OrderBook_Session, Security, SecurityCategory_CategoryCode } from '../../proto/generated/security'
import { Box, Divider } from "@mui/material"
import { getSecurityKey } from "../../util/security"
import { toLocaleStringIn } from "../../util/general"
import OrderBookItem from "../orderBook/OrderBookItem"
import { formatLargeNumber } from "../../util/screener"
import { formatUnixTimestampForOrderBook, isUnixTimestampToday } from "../../util/dateTime"
import { useOrderBookOverviewTick } from "../../hooks/useSecurityMarketTicks"
import SecurityRange from "../securityInfo/SecurityRange"

export type OrderBookOverviewProps = {
    security: Security
}

const formatPrice = (value: number | undefined, priceMultiplier: number) =>
    value != null ? toLocaleStringIn(value / priceMultiplier) : '-'

const formatQty = (value: number | undefined) =>
    value != null ? formatLargeNumber(value) : '-'

const isAuctionSession = (session: OrderBook_Session) =>
    session === OrderBook_Session.PRE_OPEN
    || session === OrderBook_Session.CALL_AUCTION
    || session === OrderBook_Session.CLOSING_AUCTION

const isIndexIndicativeSession = (session: OrderBook_Session) =>
    session === OrderBook_Session.PRE_OPEN
    || session === OrderBook_Session.CLOSING_AUCTION

const TwoColRow = ({
    left,
    right,
}: {
    left?: ReactNode
    right?: ReactNode
}) => (
    <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
        <Box width="50%" minWidth={0}>{left}</Box>
        <Box width="50%" minWidth={0}>{right}</Box>
    </Box>
)

const OrderBookOverview: React.FC<OrderBookOverviewProps> = ({ security }) => {
    const securityKey = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const orderBook = useOrderBookOverviewTick(securityKey)
    const priceMultiplier = security?.priceMultiplier ?? 100
    const isIndex = security.categoryCode === SecurityCategory_CategoryCode.INDEX

    if (!orderBook) return null

    const showAuction = !isIndex && !!orderBook.auction && isAuctionSession(orderBook.session)
    const showIndexIndicativePrice = isIndex
        && orderBook.auction?.indicativePrice != null
        && isIndexIndicativeSession(orderBook.session)
    const showReferencePrice = orderBook.session === OrderBook_Session.CLOSING_AUCTION
    const showIndicative = showAuction || showIndexIndicativePrice
    const lttFull = orderBook.lastTradedTime ? formatUnixTimestampForOrderBook(orderBook.lastTradedTime) : ''
    const lttDisplay = orderBook.lastTradedTime
        ? formatUnixTimestampForOrderBook(orderBook.lastTradedTime, isUnixTimestampToday(orderBook.lastTradedTime))
        : '-'

    return (
        <Box display="flex" flexDirection="column" width="100%">
            <SecurityRange security={security} />

            <TwoColRow
                left={<OrderBookItem title="Open" value={formatPrice(orderBook.open, priceMultiplier)} />}
                right={<OrderBookItem title="Prev. Close" value={formatPrice(orderBook.close, priceMultiplier)} />}
            />

            {!isIndex && (
                <>
                    <TwoColRow
                        left={<OrderBookItem title="Volume" value={formatLargeNumber(orderBook.volumeTradedToday)} />}
                        right={<OrderBookItem title="Avg. Price" value={formatPrice(orderBook.averageTradedPrice || undefined, priceMultiplier)} />}
                    />
                    <TwoColRow
                        left={<OrderBookItem title="Lower Circuit" value={formatPrice(orderBook.lowerCircuit, priceMultiplier)} />}
                        right={<OrderBookItem title="Upper Circuit" value={formatPrice(orderBook.upperCircuit, priceMultiplier)} />}
                    />
                    <TwoColRow
                        left={<OrderBookItem title="LTQ" value={formatQty(orderBook.lastTradedQuantity || undefined)} />}
                        right={
                            <OrderBookItem
                                title="LTT"
                                value={lttDisplay}
                                tooltip={lttFull || undefined}
                            />
                        }
                    />
                </>
            )}

            {(orderBook.openInterest != null || orderBook.inav != null) && (
                <TwoColRow
                    left={
                        orderBook.openInterest != null ? (
                            <OrderBookItem title="OI" value={formatQty(orderBook.openInterest)} />
                        ) : (
                            <OrderBookItem title="iNAV" value={formatPrice(orderBook.inav, priceMultiplier)} />
                        )
                    }
                    right={
                        orderBook.openInterest != null && orderBook.inav != null ? (
                            <OrderBookItem title="iNAV" value={formatPrice(orderBook.inav, priceMultiplier)} />
                        ) : undefined
                    }
                />
            )}

            {showIndicative && (
                <Divider sx={{ my: 0.5, borderColor: (theme) => theme.palette.divider }} />
            )}

            {showIndexIndicativePrice && (
                <TwoColRow
                    left={
                        <OrderBookItem
                            title="Indicative Price"
                            value={formatPrice(orderBook.auction?.indicativePrice, priceMultiplier)}
                        />
                    }
                />
            )}

            {showAuction && orderBook.auction && (
                <>
                    <TwoColRow
                        left={
                            <OrderBookItem
                                title="Indicative Price"
                                value={formatPrice(orderBook.auction.indicativePrice, priceMultiplier)}
                            />
                        }
                        right={
                            <OrderBookItem
                                title="Indicative Qty"
                                value={formatQty(orderBook.auction.indicativeQuantity)}
                            />
                        }
                    />
                    <TwoColRow
                        left={
                            <OrderBookItem
                                title="Total Imbalance"
                                value={formatQty(orderBook.auction.totalImbalanceQuantity)}
                            />
                        }
                        right={
                            <OrderBookItem
                                title="Market Imbalance"
                                value={formatQty(orderBook.auction.marketImbalanceQuantity)}
                            />
                        }
                    />
                    {showReferencePrice && (
                        <TwoColRow
                            left={
                                <OrderBookItem
                                    title="Reference Price"
                                    value={formatPrice(orderBook.auction.referencePrice, priceMultiplier)}
                                />
                            }
                        />
                    )}
                </>
            )}
        </Box>
    )
}

export default memo(OrderBookOverview)
